import { ORG } from "./demo-data.ts";

/** Post-login SPA route. Better Auth must receive this as a relative path. */
export const MISSION_HOME = `/o/${ORG.slug}`;

const ORIGIN_MISMATCH_RE =
  /INVALID_ORIGIN|INVALID_CALLBACK_URL|Invalid origin|Invalid callbackURL/i;

/**
 * Better Auth `callbackURL` after social or email sign-in.
 *
 * Relative paths are allowed (`allowRelativePaths`). Absolute
 * `window.location.origin + path` fails origin checks when the browser host
 * ≠ `APP_ORIGIN` (`Invalid origin` / `Invalid callbackURL`).
 */
export function missionAuthCallbackURL(path: string = MISSION_HOME): string {
  if (!path.startsWith("/") || path.startsWith("//") || path.includes("://")) {
    throw new Error("callbackURL must be a relative path (e.g. /o/pcbx)");
  }
  return path;
}

function authErrorBlob(data: unknown, depth = 0): string {
  if (typeof data === "string") return data;
  if (!data || typeof data !== "object" || depth > 4) return "";
  const obj = data as Record<string, unknown>;
  const parts: string[] = [];
  for (const key of ["code", "message", "error", "status"]) {
    const value = obj[key];
    if (typeof value === "string") parts.push(value);
    else if (value && typeof value === "object") parts.push(authErrorBlob(value, depth + 1));
  }
  return parts.join(" ");
}

export function isAuthOriginMismatch(data: unknown): boolean {
  return ORIGIN_MISMATCH_RE.test(authErrorBlob(data));
}

export function authOriginMismatchMessage(appOrigin?: string | null): string {
  const origin = (appOrigin || "").replace(/\/+$/, "");
  const where = origin ? `${origin}/login` : "the APP_ORIGIN URL (staging workers.dev /login)";
  return `Invalid origin. Open ${where} and clear site cookies for that host, then try GitHub or Google again.`;
}

export function parseAuthError(data: unknown, fallback: string, appOrigin?: string | null): string {
  if (isAuthOriginMismatch(data)) return authOriginMismatchMessage(appOrigin);
  if (!data || typeof data !== "object") return fallback;
  const body = data as {
    message?: string;
    error?: string | { message?: string };
    code?: string;
  };
  if (typeof body.message === "string" && body.message) return body.message;
  if (typeof body.error === "string" && body.error) return body.error;
  if (body.error && typeof body.error === "object" && body.error.message) return body.error.message;
  return fallback;
}
